import React, { useState } from 'react';
import { Box, Typography, IconButton, CircularProgress } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { colors } from '../../../../styles/theme';
import {
  getYoutubeThumbnailUrl,
  isValidYoutubeVideoId,
  normalizeListingVideos,
} from '../../../utils/listingVideoUtils';
import ListingVideoLightbox from './ListingVideoLightbox';
import { useYoutubeOrientations, YT_ORIENTATION } from '../../../hooks/useYoutubeOrientation';

/** Alto máximo de la grilla (vertical solo y fila justificada). */
export const VERTICAL_VIDEO_MAX_HEIGHT = 360;
export const ROW_MAX_HEIGHT = 360;
const GAP_PX = 8;
const RATIO = { [YT_ORIENTATION.HORIZONTAL]: 16 / 9, [YT_ORIENTATION.VERTICAL]: 9 / 16 };
// Fila justificada solo si la suma de ratios <= 2 horizontales: así el alto
// nunca queda más chico que el de la fila de 2 visibles actual.
const JUSTIFY_MAX_RATIO_SUM = 2 * (16 / 9) + 1e-6;
// Fila deslizable: ancho horizontal W = (100% - 8px) / 2, alto W * 9/16.
// Vertical con el mismo alto: ancho = alto * 9/16 = W * 81/256.
const MULTI_HORIZONTAL_WIDTH = 'calc((100% - 8px) / 2)';
const MULTI_VERTICAL_WIDTH = 'calc((100% - 8px) / 2 * 81 / 256)';

/**
 * Layout de la grilla:
 * - single: 1 video (horizontal ancho completo; vertical 9:16 centrado, máx. 360px de alto).
 * - justify: 2+ que entran (suma de ratios <= 2 horizontales). Mismo alto para
 *   todos, flex-grow proporcional al ratio → la fila llena el ancho. Alto máx.
 *   360px (maxWidth de la fila); si no llega al ancho, centrada.
 * - scroll: muchos / no entran. Scroll horizontal con regla de 2 visibles.
 */
export const resolveVideoRowLayout = (orientations) => {
  if (orientations.length <= 1) return 'single';
  const sum = orientations.reduce((acc, o) => acc + RATIO[o], 0);
  return sum <= JUSTIFY_MAX_RATIO_SUM ? 'justify' : 'scroll';
};

const tileSx = (layout, orientation) => {
  const vertical = orientation === YT_ORIENTATION.VERTICAL;
  const aspectRatio = vertical ? '9 / 16' : '16 / 9';
  if (layout === 'justify') {
    return { flex: `${RATIO[orientation]} 1 0`, minWidth: 0, aspectRatio };
  }
  if (layout === 'scroll') {
    const width = vertical ? MULTI_VERTICAL_WIDTH : MULTI_HORIZONTAL_WIDTH;
    return { flex: `0 0 ${width}`, width, minWidth: width, scrollSnapAlign: 'start', aspectRatio };
  }
  if (vertical) {
    return {
      flex: '0 0 auto',
      alignSelf: 'center',
      width: `min(100%, calc(${VERTICAL_VIDEO_MAX_HEIGHT}px * 9 / 16))`,
      aspectRatio,
    };
  }
  return { flex: '1 1 auto', width: '100%', aspectRatio };
};

/**
 * Preview estático: thumbnail YouTube + play blanco translúcido.
 * Fallback maxres → hqdefault si maxres no existe.
 */
function VideoPreviewThumb({ videoId }) {
  const [src, setSrc] = useState(
    () => getYoutubeThumbnailUrl(videoId, 'maxresdefault')
      || getYoutubeThumbnailUrl(videoId, 'hqdefault'),
  );

  if (!src) return null;

  return (
    <Box
      component="img"
      src={src}
      alt=""
      loading="lazy"
      onLoad={(event) => {
        // maxres a veces responde 200 con placeholder gris chico
        if (event.currentTarget.naturalWidth <= 120) {
          const hq = getYoutubeThumbnailUrl(videoId, 'hqdefault');
          if (hq && src !== hq) setSrc(hq);
        }
      }}
      onError={() => {
        const hq = getYoutubeThumbnailUrl(videoId, 'hqdefault');
        if (hq && src !== hq) setSrc(hq);
      }}
      sx={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
        bgcolor: 'rgba(0,0,0,0.35)',
      }}
    />
  );
}

/**
 * Lista de videos YouTube (solo lectura o con borrado si canEdit).
 * 1 video: ancho completo. 2+: fila compacta (2 a la vista) con scroll
 * horizontal si hay más. La grilla NO monta iframes (evita play rojo de YT);
 * click abre lightbox con el player real.
 */
export default function ListingVideoEmbeds({
  videos = [],
  canEdit = false,
  deletingIds = null,
  onDelete = null,
}) {
  const list = normalizeListingVideos(videos);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const orientations = useYoutubeOrientations(list);

  if (list.length === 0) return null;

  const multi = list.length > 1;
  const layout = resolveVideoRowLayout(orientations);
  const scroll = layout === 'scroll';
  const ratioSum = orientations.reduce((acc, o) => acc + RATIO[o], 0);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box
        data-testid="listing-video-row"
        data-layout={layout}
        sx={{
          display: 'flex',
          flexDirection: multi ? 'row' : 'column',
          flexWrap: 'nowrap',
          gap: 1,
          width: '100%',
          // justify: alto máx. ROW_MAX_HEIGHT → ancho máx. = alto × Σratios + gaps; centrada.
          maxWidth: layout === 'justify'
            ? `${Math.round(ROW_MAX_HEIGHT * ratioSum + GAP_PX * (list.length - 1))}px`
            : undefined,
          mx: layout === 'justify' ? 'auto' : undefined,
          overflowX: scroll ? 'auto' : 'visible',
          overflowY: 'hidden',
          scrollSnapType: scroll ? 'x mandatory' : undefined,
          WebkitOverflowScrolling: 'touch',
          pb: scroll ? 0.5 : 0,
          '&::-webkit-scrollbar': { height: 4 },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(255,255,255,0.22)',
            borderRadius: 2,
          },
        }}
      >
        {list.map((video, index) => {
          const videoId = video.youtube_video_id;
          if (!isValidYoutubeVideoId(videoId)) return null;
          const isDeleting = deletingIds?.has?.(video.id);

          return (
            <Box
              key={video.id || videoId}
              data-orientation={orientations[index]}
              role="button"
              tabIndex={0}
              aria-label="Ampliar video"
              onClick={() => {
                if (isDeleting) return;
                setLightboxIndex(index);
              }}
              onKeyDown={(event) => {
                if (isDeleting) return;
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setLightboxIndex(index);
                }
              }}
              sx={{
                position: 'relative',
                ...tileSx(layout, orientations[index]),
                borderRadius: 1.5,
                overflow: 'hidden',
                border: '1px solid rgba(255,255,255,0.08)',
                bgcolor: 'rgba(255,255,255,0.04)',
                cursor: isDeleting ? 'default' : 'pointer',
                opacity: isDeleting ? 0.55 : 1,
                transition: 'opacity 0.2s ease, border-color 0.2s ease',
                '&:hover .play-overlay': {
                  bgcolor: 'rgba(255,255,255,0.96)',
                  transform: 'translate(-50%, -50%) scale(1.06)',
                },
                '&:focus-visible': {
                  outline: `2px solid ${colors.primary}`,
                  outlineOffset: 2,
                },
              }}
            >
              <VideoPreviewThumb videoId={videoId} />

              {/* Play blanco translúcido (no el botón rojo de YouTube) */}
              <Box
                className="play-overlay"
                aria-hidden
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: multi ? 40 : 52,
                  height: multi ? 40 : 52,
                  borderRadius: '50%',
                  bgcolor: 'rgba(255,255,255,0.88)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.35)',
                  pointerEvents: 'none',
                  transition: 'background-color 0.15s ease, transform 0.15s ease',
                }}
              >
                <PlayArrowIcon
                  sx={{
                    fontSize: multi ? 26 : 34,
                    color: 'rgba(0,0,0,0.78)',
                    ml: '2px',
                  }}
                />
              </Box>

              {canEdit && typeof onDelete === 'function' && (
                <IconButton
                  size="small"
                  aria-label="Eliminar video"
                  disabled={isDeleting}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onDelete(video);
                  }}
                  sx={{
                    position: 'absolute',
                    top: 6,
                    right: 6,
                    zIndex: 1,
                    width: 28,
                    height: 28,
                    color: colors.textPrimary,
                    bgcolor: 'rgba(0,0,0,0.55)',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.75)', color: colors.textPrimary },
                    '&.Mui-disabled': {
                      color: 'rgba(255,255,255,0.4)',
                      bgcolor: 'rgba(0,0,0,0.4)',
                    },
                  }}
                >
                  {isDeleting ? (
                    <CircularProgress size={14} sx={{ color: 'rgba(255,255,255,0.7)' }} />
                  ) : (
                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                  )}
                </IconButton>
              )}
            </Box>
          );
        })}
      </Box>

      {multi && (
        <Typography
          variant="caption"
          sx={{
            color: 'rgba(255,255,255,0.28)',
            fontSize: '10px',
            textAlign: 'center',
          }}
        >
          {scroll
            ? `${list.length} videos · deslizá para ver más · tocá para ampliar`
            : `${list.length} videos · tocá para ampliar`}
        </Typography>
      )}

      {lightboxIndex != null ? (
        <ListingVideoLightbox
          videos={list}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onIndexChange={setLightboxIndex}
        />
      ) : null}
    </Box>
  );
}
